import { BACKEND_URL } from '@/init/settings'
import type { DocView } from '@/models/doc'
import type {
  ApiResult,
  DocListItem,
  DocsUserDataResponse,
  DocUserData,
  ListItemType,
} from '@/models/interfaces'

export const BOOKS_CACHE = 'shortform-books-list-cache'
export const BOOK_CACHE = 'shortform-book-cache'
export const ARTICLES_CACHE = 'shortform-articles-list-cache'
export const ARTICLE_CACHE = 'shortform-article-cache'
export const PODCASTS_CACHE = 'shortform-podcasts-list-cache'
export const PODCAST_CACHE = 'shortform-podcast-cache'
export const FOLLOWED_PODCAST_EPISODES_CACHE =
  'shortform-followed-podcast-episodes-cache'
export const NEW_ARTICLES_CACHE = 'shortform-new-articles-cache'
export const HOME_CACHE = 'shortform-home-docs-list-cache'
export const HIGHLIGHTS_CACHE = 'shortform-all-highlights-cache'
export const AUDIO_CACHE = 'shortform-audio'
export const PODCAST_EPISODES_CACHE = 'shortform-podcast-episodes-cache'
export const TAGS_CACHE = 'shortform-tags-cache'
export const COLLECTIONS_CACHE = 'shortform-collections-cache'
export const CUSTOM_LISTS_CACHE = 'shortform-custom-lists-cache'
export const CUSTOM_LIST_CACHE = 'shortform-custom-list-cache'
export const BOOK_OF_THE_DAY_CACHE = 'shortform-book-of-the-day-cache'
export const NEW_RELEASES_CACHE = 'shortform-new-releases-cache'
export const RECOMMENDATIONS_CACHE = 'shortform-recommendations-cache'
export const EXCERPT_CACHE = 'shortform-excerpt-cache'
export const DOCS_USER_DATA_CACHE = 'shortform-docs-user-data-cache'
export const DOC_USER_DATA_CACHE = 'shortform-doc-user-data-cache'

// User-specific caches that should be cleared on logout
export const USER_SPECIFIC_CACHES = [
  HIGHLIGHTS_CACHE,
  FOLLOWED_PODCAST_EPISODES_CACHE,
  CUSTOM_LISTS_CACHE,
  CUSTOM_LIST_CACHE,
  RECOMMENDATIONS_CACHE,
  DOCS_USER_DATA_CACHE,
  DOC_USER_DATA_CACHE,
]

export const DOC_KEYS = {
  collection: [COLLECTIONS_CACHE],
  book: [HOME_CACHE, BOOKS_CACHE, BOOK_CACHE],
  article: [HOME_CACHE, ARTICLES_CACHE, ARTICLE_CACHE, NEW_ARTICLES_CACHE],
  podcast: [
    HOME_CACHE,
    PODCASTS_CACHE,
    PODCAST_CACHE,
    FOLLOWED_PODCAST_EPISODES_CACHE,
  ],
  podcast_episode: [
    HOME_CACHE,
    PODCAST_CACHE,
    FOLLOWED_PODCAST_EPISODES_CACHE,
    PODCAST_EPISODES_CACHE,
  ],
  custom_list: [CUSTOM_LISTS_CACHE, CUSTOM_LIST_CACHE],
}

export const APP_KEYS = [
  BOOKS_CACHE,
  BOOK_CACHE,
  ARTICLES_CACHE,
  ARTICLE_CACHE,
  PODCASTS_CACHE,
  PODCAST_CACHE,
  FOLLOWED_PODCAST_EPISODES_CACHE,
  HOME_CACHE,
  HIGHLIGHTS_CACHE,
  AUDIO_CACHE,
  PODCAST_EPISODES_CACHE,
  TAGS_CACHE,
  COLLECTIONS_CACHE,
  CUSTOM_LISTS_CACHE,
  CUSTOM_LIST_CACHE,
  BOOK_OF_THE_DAY_CACHE,
  DOCS_USER_DATA_CACHE,
  DOC_USER_DATA_CACHE,
]

/**
 * Reset whole app cache.
 *
 * We do this when the user switches from free to paid plan.
 */
export async function resetAppCache(): Promise<void> {
  await resetCacheByKeys(APP_KEYS)
}

/**
 * Reset entire cache.
 */
export async function resetEntireCache(): Promise<void> {
  const keys = await caches.keys()
  await resetCacheByKeys(keys)
}

/**
 * Reset cache by given `keys`.
 */
export async function resetCacheByKeys(keys: string[]): Promise<void> {
  if (await cachesNotAvailable()) {
    return
  }
  // The `caches` is browser CacheStorage:
  // https://developer.mozilla.org/en-US/docs/Web/API/CacheStorage
  await Promise.all(keys.map((cacheName) => caches.delete(cacheName)))
}

/**
 * Update the cached user data for the doc with the given `docId` with the given `docUserData`.
 */
export async function updateDocsUserDataCache(
  docId: string,
  docUserData: DocUserData,
): Promise<void> {
  await updateCache(
    DOCS_USER_DATA_CACHE,
    BACKEND_URL + '/api/home/docs/user',
    (cachedResponse: ApiResult<DocsUserDataResponse>) => {
      return transformDocsUserData(docId, docUserData, cachedResponse)
    },
  )
}

/**
 * Update (or set) the user data entry for the given doc in `newData` in the user data cache.
 */
function transformDocsUserData(
  docId: string,
  newData: DocUserData,
  cachedResponse: ApiResult<DocsUserDataResponse>,
): ApiResult<DocsUserDataResponse> {
  cachedResponse.data[docId] = {
    list: newData.list,
    custom_lists: newData.custom_lists,
    is_favorite: newData.is_favorite,
    is_exported: newData.is_exported,
    is_followed: newData.is_followed,
    is_read: newData.is_read,
    accessed: newData.accessed,
    progress: newData.progress,
    position_chapter: newData.position_chapter,
    position_percent: newData.position_percent,
    audio_position: newData.audio_position,
  }
  return cachedResponse
}

/**
 * Add the given `doc` to the cached response of the `/api/home/docs` endpoint.
 */
export async function updateUserDocsCache(
  doc: DocListItem | DocView,
): Promise<void> {
  await updateCache(
    HOME_CACHE,
    BACKEND_URL + '/api/home/docs',
    (cachedResponse: DocListItem[]) => {
      if ('asListItem' in doc) {
        return transformUserDocs(cachedResponse, doc.asListItem)
      } else {
        return transformUserDocs(cachedResponse, doc)
      }
    },
  )
}

function transformUserDocs(
  cachedResponse: DocListItem[],
  doc: DocListItem,
): DocListItem[] {
  const alreadyExists = cachedResponse.some(
    (cachedDoc) => cachedDoc.id === doc.id,
  )
  if (!alreadyExists) {
    cachedResponse.push({
      ...doc,
      // Remove the `user` property, if it exists.
      user: undefined,
    })
  }
  return cachedResponse
}

/**
 * Create `Response` object that is used as CacheStorage value.
 */
export function createResponse(json: any): Response {
  const init = { status: 200, statusText: 'Manual Response' }
  return new Response(JSON.stringify(json, null, 2), init)
}

/**
 * Update the value at key `url` in cache `cacheName` with the result of the provided function.
 * When `url` is not a key of the cache, the cache is left as is.
 *
 * Cached data is stored as a `Response` object.
 * To update it, we read existing `Response`, run json content from it
 * through the `tranformFn` and then create new `Response` object
 * and replace the old one in the cache.
 */
export async function updateCache<T>(
  cacheName: string,
  url: string,
  transformFn: (cachedResponse: T) => T,
): Promise<void> {
  if (await cachesNotAvailable()) {
    return
  }

  // Get existing cached `Response`.
  const cache: Cache = await caches.open(cacheName)
  const resp: Response | undefined = await cache.match(url)
  if (!resp) {
    // No response in the cache, exit.
    return
  }

  // Read response json and pass to `transformFn` to
  // analyze and update the data.
  const jsonResp = transformFn(await resp.json())

  // Create modified `Response` object with updated json data
  // as a content.
  const updatedResponse = createResponse(jsonResp)

  // Update response in the cache.
  await cache.put(url, updatedResponse)
}

async function cachesNotAvailable(): Promise<boolean> {
  let isAvailable = !(typeof caches === 'undefined' || !caches)
  if (isAvailable) {
    try {
      // Check CacheStorage object, it doesn't work in Firefox Private Mode.
      // Some of the CacheStorage methods (keys(), open(), delete())
      // raise the "The operation is insecure" exception.
      await caches.keys()
    } catch (error: any) {
      if (error.message === 'The operation is insecure.') {
        isAvailable = false
      } else {
        throw error
      }
    }
  }
  return !isAvailable
}

/**
 * Reset the Custom Lists cache along with the cache for the given `docType`.
 */
export async function resetCustomListsCache(
  docType?: ListItemType,
): Promise<void> {
  await resetCacheByKeys(DOC_KEYS['custom_list'])
  if (docType) {
    await resetCacheByKeys(DOC_KEYS[docType])
  }
}

/**
 * Reset the cached response for the followed podcasts endpoint.
 */
export async function resetFollowedPodcastsCache(): Promise<void> {
  await resetCacheByKeys([FOLLOWED_PODCAST_EPISODES_CACHE])
}
